import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthService } from '@rind/auth';

/**
 * In-memory tenantId → display label cache.
 * names from Inventory API payloads (e.g. trade-partner list/detail).
 * The current session tenant is labeled from {@link AuthService.locationCode}.
 */
@Injectable({ providedIn: 'root' })
export class TenantDirectoryService {
  private readonly auth = inject(AuthService);
  private readonly namesSignal = signal<ReadonlyMap<string, string>>(new Map());

  readonly names = this.namesSignal.asReadonly();

  constructor() {
    effect(() => {
      const session = this.auth.session();
      if (!session?.tenantId) {
        this.clear();
        return;
      }

      const label = session.locationCode?.trim();
      if (label) {
        this.upsert([{ id: session.tenantId, name: label }]);
      }
    });
  }

  nameOf(tenantId: string | null | undefined): string {
    const id = normalizeTenantId(tenantId);
    if (!id) {
      return '—';
    }

    return this.namesSignal().get(id) ?? shortTenantId(id);
  }

  upsert(entries: Iterable<{ id: string; name: string }>): void {
    let changed = false;
    const next = new Map(this.namesSignal());

    for (const entry of entries) {
      const id = normalizeTenantId(entry.id);
      const name = entry.name?.trim();
      if (!id || !name) {
        continue;
      }

      if (next.get(id) !== name) {
        next.set(id, name);
        changed = true;
      }
    }

    if (changed) {
      this.namesSignal.set(next);
    }
  }

  /**
   * Caches display labels for trade-partner tenants from API payloads so
   * {@link TenantNamePipe} can resolve names after list/detail loads.
   */
  rememberTradePartners(
    partners: Iterable<{
      partnerTenantId: string;
      partnerName?: string | null;
      partnerLocationCode?: string | null;
    }>,
  ): void {
    this.upsert(
      [...partners].flatMap((partner) => {
        const name = partner.partnerName?.trim() || partner.partnerLocationCode?.trim();
        if (!name) {
          return [];
        }
        return [{ id: partner.partnerTenantId, name }];
      }),
    );
  }

  clear(): void {
    this.namesSignal.set(new Map());
  }
}

export function normalizeTenantId(tenantId: string | null | undefined): string | null {
  if (!tenantId?.trim()) {
    return null;
  }

  return tenantId.trim().toLowerCase();
}

export function shortTenantId(tenantId: string): string {
  const id = normalizeTenantId(tenantId) ?? tenantId;
  return id.length > 8 ? id.slice(0, 8) : id;
}
